import type { types } from 'mediasoup-client';
import { serializeError } from 'serialize-error';
import { SECOND_IN_MS, TEN_SECONDS_IN_MS } from '../../../constants/common';
import { DEFAULT_WEBCAM_SIMULCAST_ENCODINGS } from '../../../constants/encodings';
import { CLIENT_EVENTS, INTERNAL_CLIENT_EVENTS } from '../../../constants/events';
import type EnhancedEventEmitter from '../../../EnhancedEventEmitter';
import { isVp9 } from '../../../helpers/media';
import { withTimeout } from '../../../helpers/withTimeout';
import {
  TrackLabel,
  type VideoCodec,
  type VideoEncoderConfig,
} from '../../../types/common';
import type { ClientObserverEvents } from '../../../types/engine';
import type {
  BaseTrack,
  BaseTrackDependenciesParams,
  BaseTrackParams,
  VideoTrack,
  VideoTrackPublishParams,
} from '../../../types/media';
import type EffectsSDKTrackProcessor from '../streamEffects/video/esdk/TrackProcessor';
import type { Effects } from '../streamEffects/video/esdk/TrackProcessor';
import DefaultBaseTrack, { type LoggerContext } from './DefaultBaseTrack';

export type VideoTrackDependenciesParams = BaseTrackDependenciesParams & {
  clientEventEmitter: EnhancedEventEmitter<ClientObserverEvents>;
};

type VideoTrackParams = BaseTrackParams & VideoTrackDependenciesParams;

interface VLoggerContext extends LoggerContext {
  isEffectsProcessing: boolean;
}

const DEFAULT_BITRATE = 1000;

class DefaultVideoTrack extends DefaultBaseTrack implements VideoTrack {
  public transformParams = {};

  #trackProcessor?: EffectsSDKTrackProcessor;

  readonly #params: VideoTrackParams;

  readonly #clientEventEmitter: EnhancedEventEmitter<ClientObserverEvents>;

  constructor(params: VideoTrackParams) {
    super(params);
    this.#params = params;
    this.#clientEventEmitter = params.clientEventEmitter;
  }

  get logCtx(): VLoggerContext {
    return {
      label: this.getLabel(),
      id: this.id,
      kind: this.kind,
      producerId: this.producerId,
      paused: this.isPaused,
      published: this.isPublished,
      isEffectsProcessing: this.isEffectsProcessing,
    };
  }

  get isEffectsProcessing(): boolean {
    return Boolean(this.#trackProcessor);
  }

  getEncoderConfig(): VideoEncoderConfig {
    return this.encoderConfig;
  }

  getCodecOptions(): types.ProducerCodecOptions {
    return {
      videoGoogleStartBitrate: this.getEncoderConfig().videoGoogleStartBitrate ?? DEFAULT_BITRATE,
    };
  }

  getPreferredCodec(): VideoCodec {
    const { preferredCodec } = this.getEncoderConfig();

    if (preferredCodec) {
      return preferredCodec;
    }

    if (this.getLabel() === TrackLabel.ScreenVideo) {
      return 'vp8';
    }

    return 'h264';
  }

  getEncodings(): types.RtpEncodingParameters[] {
    return (this.getEncoderConfig().encodings ?? DEFAULT_WEBCAM_SIMULCAST_ENCODINGS);
  }

  setTransformParams(transformParams: object): void {
    this.transformParams = transformParams;
  }

  async setMaxSpatialLayer(spatialLayer: number): Promise<void> {
    const producer = this.getProducer();

    if (!producer) {
      this.logger.debug('setMaxSpatialLayer()', { message: 'Not produced yet' });

      return;
    }

    await producer.setMaxSpatialLayer(spatialLayer);
    this.logger.debug('setMaxSpatialLayer()', {
      label: this.getLabel(),
      spatialLayer,
    });
  }

  getMaxSpatialLayer(): number | undefined {
    const producer = this.getProducer();

    if (!producer) {
      this.logger.debug('getMaxSpatialLayer()', { message: 'Not produced yet' });

      return undefined;
    }

    return producer.maxSpatialLayer;
  }

  #enableLock = false;

  async enableEffects(): Promise<void> {
    if (this.#enableLock) {
      return;
    }

    this.#enableLock = true;

    try {
      if (this.#trackProcessor) {
        this.logger.debug('enableEffects()', { description: 'Track is already processed' });

        return;
      }

      if (this.mediaStreamTrack.readyState === 'ended') {
        this.logger.debug('Track already ended', { case: 'enableEffects' });

        return;
      }

      if (!this.#params.media.effectsSDKInitialized) {
        this.logger.debug('Effects SDK not initialized', { case: 'enableEffects' });

        return;
      }

      const startTime = Date.now();
      await this.performEnableEffects();
      const endTime = Date.now();
      this.logger.info('enableEffects() time', { time: (endTime - startTime) / SECOND_IN_MS });
      this.logger.info('enableEffects()', { track: this.logCtx });
      this.#clientEventEmitter.emit(CLIENT_EVENTS.effectsEnabled);
    } catch (error) {
      this.logger.warn('enableEffects()', {
        track: this.logCtx,
        error: serializeError(error),
      });

      this.#clientEventEmitter.emit(CLIENT_EVENTS.effectsFailed, {
        cause: 'Failed to enable video effects',
        operation: 'enable',
        error,
      });

      this
        .disableEffects()
        .catch((err: unknown) => {
          this.logger.warn('Failed to this.disableEffects() in catch{}', {
            track: this.logCtx,
            error: serializeError(err),
          });
        });
    } finally {
      this.#enableLock = false;
    }
  }

  async disableEffects(restoreOriginalTrack = true): Promise<void> {
    if (!this.#trackProcessor) {
      this.logger.debug('disableEffects()', { description: 'Track is not processed' });

      return;
    }

    try {
      await this.performDisableEffects(restoreOriginalTrack);
      this.logger.info('disableEffects()', { track: this.logCtx });
      this.#clientEventEmitter.emit(CLIENT_EVENTS.effectsDisabled);
    } catch (error) {
      this.logger.warn('disableEffects()', { track: this.logCtx, error });
      this.#clientEventEmitter.emit(CLIENT_EVENTS.effectsFailed, {
        cause: 'Failed to disable video effects',
        operation: 'disable',
        error,
      });
    }
  }

  async publish(params: VideoTrackPublishParams = {}): Promise<void> {
    await this.produce({
      encodings: this.getEncodings(),
      preferredCodec: this.getPreferredCodec(),
      transformParams: this.transformParams,
      codecOptions: this.getCodecOptions(),
      keyFrameRequestDelay: params.keyFrameRequestDelay ?? 0,
    });

    this.internalEventEmitter.emit(INTERNAL_CLIENT_EVENTS.trackProduced, this as unknown as BaseTrack);
  }

  async adjustEncodingPresetToCurrentMaxSpatialLayer(maxSpatialLayer: number): Promise<void> {
    if (!isVp9(this.getPreferredCodec())) {
      return;
    }

    const { rtpSender } = this.getProducer() ?? {};

    if (!rtpSender) {
      this.logger.debug('No rtpSender found in producer to change encodings', {
        maxSpatialLayer,
        trackId: this.id,
        producerId: this.producerId,
      });

      return;
    }

    const { encodings: initialEncodings = [] } = this.getEncoderConfig();
    const targetEncoding = initialEncodings[maxSpatialLayer];

    if (!targetEncoding) {
      this.logger.warn('Skipping RTPSender params update. No target encoding found', {
        initialEncodings,
      });

      return;
    }

    this.logger.debug('Adjusting encoding parameters to max spatial layer', {
      targetEncoding,
      maxSpatialLayer,
    });

    const currentParams = rtpSender.getParameters();
    await rtpSender.setParameters({
      ...currentParams,
      encodings: [{
        ...currentParams.encodings[0],
        ...targetEncoding,
      }],
    });
  }

  async applyEffects(effects: Effects): Promise<void> {
    try {
      if (!this.isEffectsProcessing && Object.keys(effects).length > 0) {
        await this.enableEffects();
      }

      this.#trackProcessor?.updateEffects(effects);
    } catch (error) {
      this.#clientEventEmitter.emit(CLIENT_EVENTS.effectsFailed, {
        cause: 'Failed to apply video effects',
        operation: 'apply',
        error,
      });
    }
  }

  resetEffects(): void {
    this.#trackProcessor?.updateEffects({});
  }

  setOnLoadModels(callback: () => void): void {
    this.#trackProcessor?.setOnLoadModels(callback);
  }

  get modelsLoadProgress(): number {
    return this.#trackProcessor?.modelsLoadProgress ?? 0;
  }

  public async pause(): Promise<void> {
    const onStopTrack = (): void => {
      if (!this.#trackProcessor) {
        return;
      }

      this.#trackProcessor?.stopProcessing();
      this.#trackProcessor = undefined;
    };

    await this.mute(onStopTrack);
  }

  @withTimeout(TEN_SECONDS_IN_MS, 'DefaultVideoTrack.enableEffects')
  private async performEnableEffects(): Promise<void> {
    const { media } = this.#params;
    this.#trackProcessor = await media.createEffectsSDKTrackProcessor(this.getLabel());
    this.#trackProcessor.setOnRuntimeError((error) => {
      this.#clientEventEmitter.emit(CLIENT_EVENTS.effectsFailed, {
        cause: `Video effects runtime error: ${ error.message }`,
        operation: 'runtime',
        error,
      });
    });
    this.setOriginalTrack();
    const processedTrack = this.#trackProcessor.processTrack(this.mediaStreamTrack);
    await this.replaceTrack(processedTrack);
  }

  @withTimeout(TEN_SECONDS_IN_MS, 'DefaultVideoTrack.disableEffects')
  private async performDisableEffects(restoreOriginalTrack: boolean): Promise<void> {
    this.#trackProcessor?.stopProcessing();
    this.#trackProcessor = undefined;

    if (!restoreOriginalTrack || !this.originalTrack) {
      return;
    }

    await this.replaceTrack(this.originalTrack);
  }
}

export default DefaultVideoTrack;
